
import { isUsageRunningAt } from '@/api/usageTime'

export default {
  name: 'UsageChangeSummary',
  props: {
    formData: {
      type: Object,
      required: true
    },
    turnoff: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    previousCfs () {
      if (this.turnoff) {
        return this.numeric(this.formData.flow)
      }

      if (this.formData.flowUpdate && this.formData.oldUsageId) {
        return this.numeric(this.oldUsage?.flow)
      }

      return this.runningCfsForDiversionPoint(this.formData.diversionPoint)
    },
    newCfs () {
      return this.turnoff ? 0 : this.numeric(this.formData.flow)
    },
    differenceCfs () {
      return this.newCfs - this.previousCfs
    },
    taskLabel () {
      if (this.turnoff) {
        return 'Turn Off'
      }

      if (this.differenceCfs > 0 && this.previousCfs > 0) {
        return 'Increase'
      }

      if (this.differenceCfs < 0 && this.newCfs > 0) {
        return 'Decrease'
      }

      if (this.previousCfs > 0 && this.differenceCfs === 0) {
        return 'Already Running'
      }

      return 'Turn On'
    },
    taskColor () {
      if (this.taskLabel === 'Already Running') {
        return 'blue-grey'
      }

      if (this.taskLabel === 'Turn Off' || this.taskLabel === 'Decrease') {
        return 'deep-orange'
      }

      return 'success'
    },
    entryLabel () {
      if (this.turnoff) {
        return 'Off CFS'
      }

      return 'New CFS'
    },
    entryCaption () {
      if (this.turnoff) {
        return 'from your off entry'
      }

      return 'from your Flow entry'
    },
    currentCaption () {
      if (this.previousCfs > 0) {
        return 'Running now'
      }

      return 'Off - not running'
    },
    resultLabel () {
      if (this.turnoff) {
        return 'Turning Off'
      }

      if (this.differenceCfs > 0 && this.previousCfs > 0) {
        return 'Increasing'
      }

      if (this.differenceCfs < 0 && this.newCfs > 0) {
        return 'Decreasing'
      }

      if (this.previousCfs > 0 && this.differenceCfs === 0) {
        return 'No CFS Change'
      }

      return 'Turning On'
    },
    resultIcon () {
      if (this.turnoff || this.differenceCfs < 0) {
        return 'mdi-arrow-down'
      }

      return 'mdi-arrow-up'
    },
    resultIconColor () {
      if (this.turnoff || this.differenceCfs < 0) {
        return '#c2410c'
      }

      return '#287a43'
    },
    sourceLabel () {
      if (this.turnoff) {
        return `Turning off running usage ${this.formData.id || ''}`.trim()
      }

      if (this.formData.flowUpdate && this.formData.oldUsageId) {
        return `Adjusting running usage ${this.formData.oldUsageId}`
      }

      if (this.formData.order_no) {
        return `From scheduled order ${this.formData.order_no}`
      }

      return 'Field order - no scheduled office order'
    },
    differenceClass () {
      if (this.differenceCfs > 0) {
        return 'positive-change'
      }
      if (this.differenceCfs < 0) {
        return 'negative-change'
      }
      return ''
    },
    oldUsage () {
      return this.formData.oldUsageId
        ? this.$store.getters['usages/find'](this.formData.oldUsageId)
        : null
    }
  },
  methods: {
    runningCfsForDiversionPoint (diversionPoint) {
      return this.$store.getters['usages/runningUsagesFor'](diversionPoint)
        .reduce((total, usage) => total + this.numeric(usage.flow), 0)
    },
    isCurrentlyRunning (usage) {
      return isUsageRunningAt(usage)
    },
    relationId (value) {
      return value && typeof value === 'object' ? value.id : value
    },
    numeric (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    roundCfs (value) {
      return Math.round((Number(value) || 0) * 100) / 100
    },
    formatCfs (value) {
      return this.roundCfs(value).toFixed(2)
    },
    formatSignedCfs (value) {
      const rounded = this.roundCfs(value)
      return rounded > 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2)
    }
  }
}
